/**
 * 修复版数字指令 - 解决中文输入法问题
 * 限制只能输入数字和小数，小数点后最多两位
 */

export default {
    name: 'number-only-fixed',

    bind(el, binding) {
        const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
        if (!input) return;

        // 默认配置
        const defaultConfig = {
            decimal: 2,           // 小数点后位数
            allowNegative: false, // 是否允许负数
            integerOnly: false,   // 是否只允许整数
            maxValue: null,       // 最大值
            minValue: null,       // 最小值
            thousandSeparator: false, // 是否显示千分位
            allowEmpty: true      // 是否允许为空
        };

        // 解析配置
        let config = { ...defaultConfig };
        if (binding.value && typeof binding.value === 'object') {
            config = { ...config, ...binding.value };
        } else if (typeof binding.value === 'number') {
            config.decimal = binding.value;
        }

        // 处理修饰符
        if (binding.modifiers.positive) config.allowNegative = false;
        if (binding.modifiers.integer) {
            config.integerOnly = true;
            config.decimal = 0;
        }

        // 存储配置
        input._numberOnlyConfig = config;

        // 标记是否正在输入法合成
        input._isComposing = false;

        // 存储原始值，用于回滚
        let lastValidValue = '';

        /**
         * 核心过滤函数
         */
        const filterValue = (value) => {
            if (!value && value !== 0) return config.allowEmpty ? '' : '0';

            let result = value.toString();

            // 处理负数
            const isNegative = config.allowNegative && result.startsWith('-');
            if (isNegative) {
                result = '-' + result.substring(1).replace(/[^\d.]/g, '');
            } else {
                result = result.replace(/[^\d.]/g, '');
            }

            // 确保只有一个小数点
            const parts = result.split('.');
            if (parts.length > 2) {
                result = parts[0] + '.' + parts.slice(1).join('');
            }

            // 处理小数位数
            if (config.integerOnly) {
                result = parts[0];
            } else if (parts.length === 2) {
                const decimalPart = parts[1];
                if (decimalPart.length > config.decimal) {
                    result = parts[0] + '.' + decimalPart.substring(0, config.decimal);
                }
            }

            // 处理开头多余的0（排除 0.x 的情况）
            if (result.length > 1 && result.startsWith('0') && !result.startsWith('0.')) {
                result = result.substring(1);
            }

            // 处理只有负号的情况
            if (result === '-') {
                return config.allowEmpty ? '' : result;
            }

            // 验证数值范围
            const numericValue = parseFloat(result);
            if (!isNaN(numericValue)) {
                if (config.maxValue !== null && numericValue > config.maxValue) {
                    result = config.maxValue.toString();
                }
                if (config.minValue !== null && numericValue < config.minValue) {
                    result = config.minValue.toString();
                }
            }

            return result;
        };

        /**
         * 处理输入事件
         */
        const handleInput = (e) => {
            // 如果正在输入法合成，跳过处理
            if (input._isComposing) {
                return;
            }

            const value = e.target.value;
            const filtered = filterValue(value);

            // 如果值被修改，更新并触发事件
            if (value !== filtered) {
                e.target.value = filtered;
                // 记录光标位置
                const cursorPosition = e.target.selectionStart;
                // 触发输入事件
                e.target.dispatchEvent(new Event('input', { bubbles: true }));
                e.target.dispatchEvent(new Event('change', { bubbles: true }));

                // 恢复光标位置（减去被过滤的字符数）
                setTimeout(() => {
                    const adjustedPosition = Math.max(0, cursorPosition - (value.length - filtered.length));
                    e.target.setSelectionRange(adjustedPosition, adjustedPosition);
                }, 0);
            }

            // 保存最后一次有效值
            lastValidValue = filtered;
        };

        /**
         * 处理组合输入开始（中文输入法）
         */
        const handleCompositionStart = () => {
            input._isComposing = true;
            // 保存组合开始前的值
            lastValidValue = input.value;
        };

        /**
         * 处理组合输入结束（中文输入法）
         */
        const handleCompositionEnd = (e) => {
            input._isComposing = false;

            // 延迟处理，确保值已更新
            setTimeout(() => {
                const value = e.target.value;
                const filtered = filterValue(value);

                if (value !== filtered) {
                    e.target.value = filtered;
                    e.target.dispatchEvent(new Event('input', { bubbles: true }));
                    e.target.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }, 10);
        };

        /**
         * 处理键盘事件（额外保护）
         */
        const handleKeyDown = (e) => {
            // 允许所有控制键和功能键
            if (
                e.ctrlKey ||
                e.metaKey ||
                e.altKey ||
                [
                    'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
                    'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Escape',
                    'Home', 'End', 'PageUp', 'PageDown'
                ].includes(e.key)
            ) {
                return;
            }

            // 针对数字和小数点的特殊处理
            const key = e.key;
            const currentValue = input.value;
            const selectionStart = input.selectionStart;
            const selectionEnd = input.selectionEnd;

            // 检查是否允许输入
            if (!isAllowedKey(key, currentValue, selectionStart, config)) {
                e.preventDefault();
            }
        };

        /**
         * 处理粘贴事件
         */
        const handlePaste = (e) => {
            e.preventDefault();
            const pasteData = e.clipboardData.getData('text');
            const config = input._numberOnlyConfig;

            // 过滤粘贴内容
            let filtered = pasteData;
            if (config.allowNegative) {
                filtered = filtered.replace(/[^-\d.]/g, '');
            } else {
                filtered = filtered.replace(/[^\d.]/g, '');
            }

            // 进一步过滤
            filtered = filterValue(filtered);

            // 获取选区信息
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const currentValue = input.value;

            // 插入过滤后的文本
            const newValue = currentValue.substring(0, start) + filtered + currentValue.substring(end);
            input.value = newValue;

            // 触发事件
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));

            // 设置光标位置
            setTimeout(() => {
                input.setSelectionRange(start + filtered.length, start + filtered.length);
            }, 0);
        };

        /**
         * 处理失焦事件（格式化）
         */
        const handleBlur = () => {
            const value = input.value;
            if (value === '' && !config.allowEmpty) {
                input.value = '0';
                input.dispatchEvent(new Event('input', { bubbles: true }));
                return;
            }

            // 移除末尾的小数点
            if (value.endsWith('.')) {
                input.value = value.slice(0, -1);
                input.dispatchEvent(new Event('input', { bubbles: true }));
            }

            // 千分位格式化
            if (config.thousandSeparator && value && !value.includes('e') && !value.includes('E')) {
                const formatted = formatWithThousandSeparator(input.value);
                if (formatted !== input.value) {
                    input.value = formatted;
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
        };

        /**
         * 处理聚焦事件（移除千分位）
         */
        const handleFocus = () => {
            if (config.thousandSeparator) {
                const original = input.value.replace(/,/g, '');
                if (original !== input.value) {
                    const cursorPosition = input.selectionStart;
                    input.value = original;
                    input.dispatchEvent(new Event('input', { bubbles: true }));

                    // 恢复光标位置（大致位置）
                    setTimeout(() => {
                        const adjustedPosition = estimateCursorPosition(cursorPosition, original);
                        input.setSelectionRange(adjustedPosition, adjustedPosition);
                    }, 0);
                }
            }
        };

        // 绑定事件
        input.addEventListener('input', handleInput);
        input.addEventListener('compositionstart', handleCompositionStart);
        input.addEventListener('compositionend', handleCompositionEnd);
        input.addEventListener('keydown', handleKeyDown);
        input.addEventListener('paste', handlePaste);
        input.addEventListener('blur', handleBlur);
        input.addEventListener('focus', handleFocus);

        // 存储清理函数
        input._numberOnlyCleanup = () => {
            input.removeEventListener('input', handleInput);
            input.removeEventListener('compositionstart', handleCompositionStart);
            input.removeEventListener('compositionend', handleCompositionEnd);
            input.removeEventListener('keydown', handleKeyDown);
            input.removeEventListener('paste', handlePaste);
            input.removeEventListener('blur', handleBlur);
            input.removeEventListener('focus', handleFocus);
        };
    },

    componentUpdated(el, binding) {
        const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
        if (!input) return;

        // 更新配置
        let config = input._numberOnlyConfig || {};
        const newConfig = { ...config };

        if (binding.value && typeof binding.value === 'object') {
            Object.assign(newConfig, binding.value);
        }

        input._numberOnlyConfig = newConfig;
    },

    unbind(el) {
        const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
        if (!input || !input._numberOnlyCleanup) return;

        input._numberOnlyCleanup();
        delete input._numberOnlyCleanup;
        delete input._numberOnlyConfig;
        delete input._isComposing;
    }
};

/**
 * 检查按键是否允许
 */
function isAllowedKey(key, currentValue, cursorPosition, config) {
    // 允许数字
    if (/^\d$/.test(key)) {
        return true;
    }

    // 允许小数点
    if (key === '.' && !config.integerOnly) {
        // 检查是否已存在小数点
        const beforeCursor = currentValue.substring(0, cursorPosition);
        const afterCursor = currentValue.substring(cursorPosition);
        const existingDecimal = (beforeCursor + afterCursor).includes('.');
        return !existingDecimal;
    }

    // 允许负号（如果配置允许）
    if (key === '-' && config.allowNegative) {
        return cursorPosition === 0 && !currentValue.includes('-');
    }

    return false;
}

/**
 * 千分位格式化
 */
function formatWithThousandSeparator(value) {
    if (!value || value === '-') return value;

    const isNegative = value.startsWith('-');
    const numStr = isNegative ? value.substring(1) : value;

    // 分割整数和小数部分
    const parts = numStr.split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1] || '';

    // 添加千分位
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // 重新组合
    const formatted = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    return isNegative ? `-${formatted}` : formatted;
}

/**
 * 估算光标位置（用于千分位转换）
 */
function estimateCursorPosition(oldPosition, newValue) {
    // 这是一个简化的估算，实际可能需要更复杂的逻辑
    return Math.min(oldPosition, newValue.length);
}